'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import InvoiceHeader from './components/InvoiceHeader';
import InvoiceList from './components/InvoiceList';
import InvoiceBuilder from './components/InvoiceBuilder';
import { invoicesService, type Invoice } from '@/lib/services/staffingService';

export type InvoiceView = 'list' | 'create';

export default function InvoicingPage() {
  const [view, setView] = useState<InvoiceView>('list');
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await invoicesService.getAll();
      setInvoices(data);
    } catch (e: unknown) {
      const err = e as { message?: string };
      setError(err?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvoices();
    const unsub = invoicesService.subscribe(
      (inv) => setInvoices((prev) => [inv, ...prev]),
      (inv) => setInvoices((prev) => prev.map((i) => (i.id === inv.id ? inv : i))),
      (id) => setInvoices((prev) => prev.filter((i) => i.id !== id))
    );
    return unsub;
  }, [loadInvoices]);

  const handleCreate = () => {
    setEditingInvoice(null);
    setView('create');
  };

  const handleEdit = (invoice: Invoice) => {
    setEditingInvoice(invoice);
    setView('create');
  };

  const handleBack = () => {
    setEditingInvoice(null);
    setView('list');
    loadInvoices();
  };

  const handleSaved = (invoice: Invoice) => {
    setInvoices((prev) => {
      const exists = prev.find((i) => i.id === invoice.id);
      if (exists) return prev.map((i) => (i.id === invoice.id ? invoice : i));
      return [invoice, ...prev];
    });
    setView('list');
    setEditingInvoice(null);
  };

  return (
    <AppLayout>
      <div className="space-y-5 fade-in">
        <InvoiceHeader view={view} onBack={handleBack} onCreateNew={handleCreate} />
        {view === 'list' ? (
          <InvoiceList
            invoices={invoices}
            loading={loading}
            error={error}
            onEdit={handleEdit}
            onCreateNew={handleCreate}
            onStatusChange={loadInvoices}
          />
        ) : (
          <InvoiceBuilder
            editingInvoice={editingInvoice}
            onBack={handleBack}
            onSaved={handleSaved}
          />
        )}
      </div>
    </AppLayout>
  );
}
